import type { ColumnFilter } from '@tanstack/table-core';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useEffectOnce } from 'usehooks-ts';

import type { GetDisbursementPayoutRequestsParams } from 'api';

import type { GlobalFiltersState } from 'reduxLocal/slices/globalFiltersSlice';
import type { LocalState } from 'reduxLocal/slices/pages.payoutsSlice';
import {
  reset,
  selectState,
  setCursorAfter,
  setCursorBefore,
  setIncludeCursor,
  setPageSize,
  setSort,
  updateFilters as updateStoreFilters,
  setKey
} from 'reduxLocal/slices/pages.payoutsSlice';

type MergedState = LocalState & GlobalFiltersState;

export function useLocalState(defaultState?: Partial<LocalState>) {
  const state = useSelector(selectState);
  const dispatch = useDispatch();

  const queryParams = useMemo(
    () => ({
      cursorAfter: state.pagination?.cursorAfter,
      cursorBefore: state.pagination?.cursorBefore,
      pageSize: state.pagination?.pageSize,
      sort: state.sort,
      ...state.filters
    }),
    [state]
  );

  const tableFilters = useMemo<ColumnFilter[]>(() => {
    const result: ColumnFilter[] = [];
    for (const [key, value] of Object.entries(state.filters)) {
      if (value) {
        result.push({ id: key, value: value.toString() });
      }
    }

    return result;
  }, [state.filters, state.pagination]);

  const resetState = useCallback(() => dispatch(reset()), [dispatch]);

  const updateSort = useCallback(
    (value: string) => dispatch(setSort(value)),
    [dispatch]
  );

  const updateFilters = useCallback(
    (value: LocalState['filters']) => dispatch(updateStoreFilters(value)),
    [dispatch]
  );

  const updateCursorBefore = useCallback(
    (value: string | undefined) => dispatch(setCursorBefore(value)),
    [dispatch]
  );

  const updateCursorAfter = useCallback(
    (value: string | undefined) => dispatch(setCursorAfter(value)),
    [dispatch]
  );

  const updateIncludeCursor = useCallback(
    (value: boolean) => dispatch(setIncludeCursor(value)),
    [dispatch]
  );

  const updatePageSize = useCallback(
    (value: string | undefined) => dispatch(setPageSize(value)),
    [dispatch]
  );

  const updateKey = useCallback(
    (value: number) => dispatch(setKey(value)),
    [dispatch]
  );

  useEffectOnce(() => {
    if (!defaultState) return;
  });

  return {
    state,
    tableFilters,
    queryParams,
    resetState,
    updateSort,
    updateFilters,
    updateCursorBefore,
    updateCursorAfter,
    updateIncludeCursor,
    updatePageSize,
    updateKey
  };
}

// Helper function to check if a filter value should be included
const shouldIncludeFilter = (value: string | undefined): boolean => {
  return Boolean(value && value !== 'All');
};

export const stateToRequestParams = (
  state: Partial<MergedState>
): GetDisbursementPayoutRequestsParams => {
  const params = {} as GetDisbursementPayoutRequestsParams;

  // Sort
  if (state?.sort) {
    params.sort = state.sort;
  }

  // Pagination
  const { pagination } = state;
  if (pagination) {
    if (pagination.cursorAfter) params.cursor_after = pagination.cursorAfter;
    if (pagination.cursorBefore) params.cursor_before = pagination.cursorBefore;
    if (pagination.includeCursor)
      params.include_cursor = pagination.includeCursor.toString();
    if (pagination.pageSize) params.page_size = pagination.pageSize;
  }

  // Date range
  if (state?.dateRange?.from) params.created_after = state.dateRange.from;
  if (state?.dateRange?.to) params.created_before = state.dateRange.to;

  // Batch ID
  if (state?.batchId) params.batch_id = state.batchId;

  // Filters
  const { filters } = state;
  if (filters) {
    // Filters with 'All' check
    if (shouldIncludeFilter(filters.state))
      params.state_category = filters.state;
    if (shouldIncludeFilter(filters.accountNumber))
      params.virtual_account_number = filters.accountNumber;
    if (shouldIncludeFilter(filters.currency))
      params.currency_code = filters.currency;
    if (shouldIncludeFilter(filters.businessUnitId))
      params.business_unit_id = filters.businessUnitId;
    if (shouldIncludeFilter(filters.localInstrument))
      params.local_instrument = filters.localInstrument;

    // Direct filter mappings (no 'All' check needed)
    if (filters.reasonCode) params.reason_code_id = filters.reasonCode;
    if (filters.amountFrom) params.amount_from = filters.amountFrom;
    if (filters.amountTo) params.amount_to = filters.amountTo;
  }

  return params;
};
